import { ChevronDown, CircleHelp, LogOut, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { useRef, useState } from "react";
import type { UserProfile } from "@/shared/api/api-client";
import { ProfileAvatar } from "@/shared/components/ProfileAvatar";
import { useDismiss } from "@/shared/hooks/useDismiss";
import { hasRoleAtLeast, ROLE_LABEL, ROLES } from "@/shared/utils/roles";
import { navigationItems } from "@/shell/navigation";

/** O cargo de maior alcance da pessoa, na língua da tela. */
function roleLabel(profile: UserProfile): string {
  const top = ROLES.find(role => profile.roles.includes(role));
  return top ? ROLE_LABEL[top] : "Usuário";
}

export function AccountMenu({
  profile,
  pathname,
  logout,
  variant = "topbar"
}: {
  profile: UserProfile;
  pathname: string;
  logout: () => void;
  variant?: "topbar" | "sidebar";
}) {
  const [open, setOpen] = useState(false);
  const controlRef = useRef<HTMLDivElement>(null);

  const avatarContent = <ProfileAvatar name={profile.name} avatarUrl={profile.avatar_url}/>;
  const role = roleLabel(profile);
  // Os itens que moram aqui e não na sidebar (hoje, a Administração), com a
  // mesma regra de papel mínimo da navegação.
  const accountItems = navigationItems.filter(
    item => item.placement === "account" && (!item.requiresRole || hasRoleAtLeast(profile, item.requiresRole))
  );

  useDismiss(open, () => setOpen(false), { ref: controlRef });

  const option = (to: string, Icon: typeof Settings, title: string, hint: string) => (
    <Link role="menuitem" to={to} className={pathname === to ? "active" : ""} onClick={() => setOpen(false)}>
      <i className="account-menu-icon"><Icon size={16}/></i>
      <span><strong>{title}</strong><small>{hint}</small></span>
    </Link>
  );

  return (
    <div className={`account-control ${variant === "topbar" ? "account-control--topbar" : ""}`} ref={controlRef}>
      {variant === "topbar" ? (
        <button
          className="account-avatar-btn"
          data-tooltip={profile.name}
          aria-label={`Abrir menu de ${profile.name}`}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen(current => !current)}
        >
          <div className="avatar">{avatarContent}</div>
        </button>
      ) : (
        <button
          className="sidebar-user"
          data-tooltip={profile.name}
          aria-label={`Abrir menu de ${profile.name}`}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen(current => !current)}
        >
          <div className="avatar">{avatarContent}</div>
          <span><strong>{profile.name}</strong><small>{role}</small></span>
          <ChevronDown className="account-chevron" size={15}/>
        </button>
      )}
      {open && (
        <div className="account-menu" role="menu">
          <div className="account-menu-profile">
            <div className="avatar">{avatarContent}</div>
            <div>
              <strong>{profile.name}</strong>
              <span>{profile.email}</span>
              <em className="account-menu-role">{role}</em>
            </div>
          </div>
          <div className="account-menu-options">
            {option("/configuracoes", Settings, "Configurações", "Perfil, preferências e segurança")}
            {accountItems.map(item => {
              const target = item.href();
              return (
                <Link
                  key={item.label}
                  role="menuitem"
                  to={target}
                  className={pathname.startsWith(target) ? "active" : ""}
                  onClick={() => setOpen(false)}
                >
                  <i className="account-menu-icon"><item.icon size={16}/></i>
                  <span><strong>{item.label}</strong><small>Usuários e configuração geral</small></span>
                </Link>
              );
            })}
            <Link role="menuitem" to="/ajuda" state={{ from: pathname }} className={pathname === "/ajuda" ? "active" : ""} onClick={() => setOpen(false)}>
              <i className="account-menu-icon"><CircleHelp size={16}/></i>
              <span><strong>Central de ajuda</strong><small>Dúvidas e suporte</small></span>
            </Link>
          </div>
          <button className="account-logout" role="menuitem" onClick={logout}>
            <LogOut size={16}/><span>Sair da conta</span>
          </button>
        </div>
      )}
    </div>
  );
}
